/**
 * Charge Inventory — one row per actual billed invoice charge.
 *
 * The benchmark table historically listed only charges that the market
 * engine returned a comparison for. This module produces the complete
 * inventory of *legitimate* billed charges so every one of them can be
 * displayed, while suppressing the OCR noise that is not a billed charge:
 * operator/employee names, equipment or vehicle descriptions carried as
 * supporting detail, notes, narrative text, fragments and duplicated
 * invoice lines.
 *
 * OCR line text routinely arrives as several physical lines glued together
 * with newlines, e.g.
 *
 *     "Rotator operator\nidn't add a pa\nChristian"
 *
 * The first line is the billed *service family*; everything after it is
 * supporting detail (a person, a unit number, an equipment descriptor) or
 * pure OCR garbage. This module separates the two so that:
 *
 *   • market research is performed on the SERVICE FAMILY only, never on a
 *     person's name or a truck number,
 *   • separate personnel or separate billing occurrences stay separate rows,
 *   • true OCR duplicates (the same page captured twice) collapse.
 *
 * Pure and deterministic. No network, no AI, no pricing.
 */

export interface InventoryCharge {
  /** Stable identity for the charge within the invoice. */
  id: string;
  /** Original OCR description, unmodified. */
  description: string;
  /** Clean, customer-facing label: service family plus meaningful detail. */
  displayName: string;
  /** Service family alone — what market research is performed against. */
  familyName: string;
  /** Meaningful supporting detail (person, unit, equipment) or null. */
  detail: string | null;
  /** Lowercased, punctuation-folded FAMILY key used for matching/research. */
  normalized: string;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  /** Source line index in the invoice line-item array. */
  sourceIndex: number;
  /** How many identical invoice lines were collapsed into this row. */
  duplicateCount: number;
  /**
   * True when this printed source line duplicates an earlier line exactly.
   * The line is PRESERVED (printed invoices are authoritative) but is held
   * out of the benchmarked/recovery scope pending vendor clarification.
   */
  duplicateSuspect: boolean;
  /** The earlier charge this line appears to duplicate, when suspect. */
  duplicateOfChargeId: string | null;
  /**
   * WHY the line was classified as a repeat. `repeated_capture` and
   * `same_region_reread` are PROVEN — by extraction provenance, or by a whole
   * consecutive block of printed lines reappearing verbatim (a page supplied
   * twice). `identical_line_no_source_metadata` is UNCERTAIN: the line stays
   * fully in the printed inventory and is flagged for vendor clarification.
   */
  duplicateEvidence:
    | "repeated_capture"
    | "same_region_reread"
    | "identical_line_no_source_metadata"
    | null;
  /**
   * TRUE only for a PROVEN repeated extraction of a printed line. These lines
   * are the only ones held out of the certified financial scope; an uncertain
   * duplicate is never silently subtracted from the invoice.
   */
  repeatedExtraction: boolean;

  /** Extraction provenance, when the OCR provider supplied it. */
  sourceFile: number | null;
  sourcePage: number | null;
  sourceRegion: string | null;
  /**
   * Billing-period signature (storage date range, service dates) recovered
   * from the raw line. Two otherwise-identical lines covering DIFFERENT
   * periods are separate billings and must never collapse.
   */
  periodKey: string | null;

}

export interface InventoryExclusion {
  description: string;
  reason:
    | "no_billed_amount"
    | "operator_or_employee_name"
    | "equipment_detail"
    | "vehicle_description"
    | "note_or_narrative"
    | "ocr_fragment"
    | "duplicate_line";
}

export interface ChargeInventory {
  /** Every printed source line that survived non-charge filtering. */
  charges: InventoryCharge[];
  /**
   * THE CERTIFIED SCOPE. Every financial total, count, benchmark and recovery
   * figure is computed from this list and never from `charges`, so a page
   * captured twice can never inflate the invoice.
   */
  canonical: InventoryCharge[];
  /** Lines held out of the canonical scope as repeated extractions. */
  duplicateExtractions: InventoryCharge[];
  excluded: InventoryExclusion[];
}


const NUMBER_KEYS = {
  qty: ["quantity", "qty", "days", "hours", "units"],
  rate: ["unitPrice", "unit_price", "rate", "daily_rate", "hourly_rate"],
  amount: ["total", "amount", "line_total", "lineTotal", "extended", "price"],
} as const;

function readNumber(item: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim()) {
      const negative = /^\s*[(-]/.test(v) || /\bcredit\b/i.test(v);
      const n = Number(v.replace(/[^0-9.]/g, ""));
      if (Number.isFinite(n) && /[0-9]/.test(v)) return negative ? -n : n;
    }
  }
  return null;
}

export function readDescription(item: Record<string, unknown>): string {
  for (const k of ["description", "name", "item", "service", "label"]) {
    const v = item[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export function normalizeChargeKey(v: string | null | undefined): string {
  return (v ?? "")
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    // Singularize trailing plurals so a charge matches the benchmark engine's
    // key for the same service ("Administrative Fees" ≡ "Administrative Fee").
    .split(" ")
    .map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w))
    .join(" ");
}

/* ─────────────────── description parsing / normalization ─────────────────── */

/** Words that mark a line as describing a service or a piece of equipment. */
const SERVICE_WORD_RE =
  /(rotator|tow|towing|rollback|flatbed|wrecker|storage|recovery|van|tractor|trailer|truck|rigger|rigging|helper|operator|crane|winch|labor|clean|cleanup|fee|surcharge|fuel|mile|mileage|admin|dolly|dump|loader|forklift|sweep|hazmat|permit|escort|air\s?cushion|landoll|lowboy|skid|bobcat|excavat|tarp|strap|ton\b|unit\b)/i;

/** A standalone personal name carried under the service line. */
function isPersonName(line: string): boolean {
  if (SERVICE_WORD_RE.test(line)) return false;
  return /^[A-Z][a-z]{1,15}(?:\s+[A-Z][a-z]{0,15}\.?)?$/.test(line.trim());
}

/** A unit / truck / asset identifier line. */
function isUnitReference(line: string): boolean {
  return /^(unit|truck|trailer|veh|vehicle|asset|wrecker)\s*#?\s*[\w-]+/i.test(line.trim());
}

/** Date, date-range or timestamp detail; never part of the service family. */
function isDateDetail(line: string): boolean {
  return (
    /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(line) ||
    /\b\d{4}-\d{2}-\d{2}\b/.test(line) ||
    /\bthrough\b/i.test(line)
  );
}

/** Line-item fields that may carry the billing period for a charge. */
export const PERIOD_FIELD_KEYS = [
  "period",
  "dates",
  "date",
  "date_range",
  "dateRange",
  "service_date",
  "serviceDate",
  "start_date",
  "startDate",
  "end_date",
  "endDate",
  "storage_start",
  "storage_end",
  "from",
  "to",
  "detail",
  "details",
  "notes",
] as const;

const PERIOD_TOKEN_RE =
  /(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2}|[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{4})/g;

/**
 * Billing-period signature for a raw invoice line: every date token found in
 * the line (plus any explicit day/period count), order-preserved.
 *
 * Storage charges are billed per period; two Storage Heavy lines covering
 * different date ranges are two legitimate charges even when quantity, rate
 * and amount are identical. Ignoring this detail is what collapsed them.
 */
export function extractPeriodSignature(
  raw: string,
  extra: ReadonlyArray<string | null | undefined> = [],
): string | null {
  const haystack = [raw, ...extra.filter(Boolean)].join(" ");
  const dates = haystack.match(PERIOD_TOKEN_RE) ?? [];
  if (!dates.length) return null;
  return dates.map((d) => d.replace(/[.,]/g, "").toLowerCase().replace(/\s+/g, " ")).join("|");
}

/**
 * Truncated OCR spill: a mid-word or mid-sentence fragment that leaked into
 * the description cell. These carry no billing meaning at all.
 */
function isOcrNoiseLine(line: string): boolean {
  const t = line.trim();
  const letters = t.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 3) return true;
  if (/^[a-z]/.test(t) && !SERVICE_WORD_RE.test(t)) return true;
  if (/\/\)|\)\s*to\s+the\b/i.test(t)) return true;
  return false;
}

/** Strips unit numbers, parentheticals and stray punctuation from a family. */
function cleanFamilyLine(line: string): string {
  return line
    .replace(/\bunit\s*#?\s*[\w-]+/gi, " ")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[\s,;:.\-–—]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export interface ParsedChargeDescription {
  /** Service family alone, cleaned of names, unit numbers and OCR spill. */
  familyName: string;
  /** Meaningful supporting detail, or null when there is none. */
  detail: string | null;
  /** Family plus detail, for display. */
  displayName: string;
}

/**
 * Splits a raw OCR charge description into its service family and its
 * supporting detail. Research is always performed on `familyName`.
 */
export function parseChargeDescription(raw: string): ParsedChargeDescription {
  const lines = raw
    .split(/[\r\n]+/)
    .map((l) => l.trim())
    .filter(Boolean);

  const head = lines[0] ?? raw.trim();
  const familyName = cleanFamilyLine(head) || head.trim();

  let person: string | null = null;
  let unit: string | null = null;
  let descriptor: string | null = null;

  for (const line of lines.slice(1)) {
    if (isDateDetail(line)) continue;
    if (isPersonName(line)) {
      person ??= line;
      continue;
    }
    if (isUnitReference(line)) {
      unit ??= line;
      continue;
    }
    if (isOcrNoiseLine(line)) continue;
    if (SERVICE_WORD_RE.test(line)) descriptor ??= line.replace(/\s+/g, " ").trim();
  }

  const detail = person ?? unit ?? descriptor ?? null;
  return {
    familyName,
    detail,
    displayName: detail ? `${familyName} — ${detail}` : familyName,
  };
}

/* ─────────────────────── non-charge detection ─────────────────────── */

/** Operator / driver / employee attribution lines. */
const OPERATOR_RE =
  /^(operator|driver|technician|tech|employee|dispatcher|dispatched by|performed by|assigned to|supervisor|rigger name|crew member)\b\s*[:#-]?/i;

/**
 * Equipment or unit identification carried as supporting detail.
 *
 * The trailing token must be an identifier (empty, or containing a digit) —
 * an all-alphabetic tail means the line names a SERVICE, not an asset id
 * ("Equipment cleaning" is a billed charge; "Equipment #4471" is not).
 */
const EQUIPMENT_DETAIL_RE =
  /^(unit|truck|trailer|equipment|asset|vin|plate|license|tag|rotator id|wrecker id)\s*[#:]?\s*(?:[a-z]*\d[a-z0-9-]*)?$/i;

/** Vehicle being serviced, not a billed service. */
const VEHICLE_RE =
  /^(vehicle|veh|make|model|year|color|odometer|mileage reading|body type)\b\s*[:#-]?/i;

/** Free-text notes and narrative. */
const NOTE_RE =
  /^(note|notes|remark|remarks|comment|comments|description of work|narrative|summary|terms|thank you|please remit|payment due|subtotal|sub total|total|balance|amount due|tax|sales tax)\b/i;

function isFragment(description: string): boolean {
  const letters = description.replace(/[^a-zA-Z]/g, "");
  if (letters.length < 3) return true;
  // A bare number, date, or symbol run is never a charge description.
  if (/^[\d\s.,/$%()-]+$/.test(description)) return true;
  return false;
}

function classifyNonCharge(
  family: string,
  amount: number | null,
): InventoryExclusion["reason"] | null {
  if (isFragment(family)) return "ocr_fragment";
  if (OPERATOR_RE.test(family)) return "operator_or_employee_name";
  if (VEHICLE_RE.test(family)) return "vehicle_description";
  if (NOTE_RE.test(family)) return "note_or_narrative";
  if (EQUIPMENT_DETAIL_RE.test(family)) return "equipment_detail";
  // Supporting detail lines never carry their own billed amount.
  if (amount === null) return "no_billed_amount";
  return null;
}

/* ─── Extraction provenance ─── */

interface Provenance {
  file: number | null;
  page: number | null;
  region: string | null;
}

function readProvenance(item: Record<string, unknown>): Provenance {
  const num = (keys: string[]): number | null => {
    for (const k of keys) {
      const v = item[k];
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return Number(v);
    }
    return null;
  };
  const region = ["source_region", "sourceRegion", "bbox", "bounding_region"].reduce<string | null>(
    (acc, k) => acc ?? (typeof item[k] === "string" && (item[k] as string).trim() ? (item[k] as string) : null),
    null,
  );
  return {
    file: num(["source_file", "sourceFile", "file_index", "fileIndex"]),
    page: num(["source_page", "sourcePage", "page", "page_number", "pageNumber"]),
    region,
  };
}

function parseRect(region: string | null): [number, number, number, number] | null {
  if (!region) return null;
  const parts = region.split(",").map((p) => Number(p.trim()));
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0], parts[1], parts[2], parts[3]];
}

/** True when two page regions overlap — i.e. they are the same printed line. */
function regionsOverlap(a: string | null, b: string | null): boolean | null {
  const ra = parseRect(a);
  const rb = parseRect(b);
  if (!ra || !rb) return a && b ? a === b : null;
  const overlapX = Math.min(ra[2], rb[2]) - Math.max(ra[0], rb[0]);
  const overlapY = Math.min(ra[3], rb[3]) - Math.max(ra[1], rb[1]);
  return overlapX > 0 && overlapY > 0;
}

/**
 * Decides what two otherwise-identical extracted lines actually are.
 *
 * Provenance is trusted when present. Without it the match stays UNCERTAIN —
 * flagged for clarification, never silently accepted as a real second charge
 * and never silently deleted.
 */
function provenanceVerdict(
  existing: { sourceFile: number | null; sourcePage: number | null; sourceRegion: string | null },
  next: Provenance,
): "repeated_capture" | "same_region_reread" | "identical_line_no_source_metadata" | "distinct_printed_lines" {
  const haveBoth = existing.sourcePage !== null && next.page !== null;
  if (haveBoth) {
    const sameSheet =
      existing.sourcePage === next.page && (existing.sourceFile ?? null) === (next.file ?? null);
    if (sameSheet) {
      const overlap = regionsOverlap(existing.sourceRegion, next.region);
      if (overlap === true) return "same_region_reread";
      if (overlap === false) return "distinct_printed_lines";
      return "identical_line_no_source_metadata";
    }
    /* Same printed content arriving from a different page or a different
       uploaded file is the same sheet supplied twice. */
    return "repeated_capture";
  }
  return "identical_line_no_source_metadata";
}


/**
 * Builds the deduplicated inventory of billed charges.
 *
 * Two invoice lines collapse into one row only when the service family,
 * quantity, rate and amount are all identical AND their supporting details
 * are compatible — either the same detail, or one line carrying no detail at
 * all. That is the signature of the same page being captured twice.
 *
 * Two lines that name DIFFERENT personnel, different units or different
 * quantities are genuinely separate billings and always stay separate rows,
 * even when they share the same service family and the same amount.
 */
export function buildChargeInventory(
  lineItems: Array<Record<string, unknown>> = [],
): ChargeInventory {
  const charges: InventoryCharge[] = [];
  const excluded: InventoryExclusion[] = [];
  /** groupKey → rows already emitted for that family/qty/rate/amount. */
  const groups = new Map<string, InventoryCharge[]>();

  lineItems.forEach((item, index) => {
    if (!item || typeof item !== "object") return;
    const description = readDescription(item);
    if (!description) return;

    const quantity = readNumber(item, NUMBER_KEYS.qty);
    const rate = readNumber(item, NUMBER_KEYS.rate);
    let amount = readNumber(item, NUMBER_KEYS.amount);
    if (amount === null && quantity !== null && rate !== null) {
      amount = Math.round(quantity * rate * 100) / 100;
    }

    const parsed = parseChargeDescription(description);
    const reason = classifyNonCharge(parsed.familyName, amount);
    if (reason) {
      excluded.push({ description, reason });
      return;
    }

    const normalized = normalizeChargeKey(parsed.familyName);
    const detailKey = normalizeChargeKey(parsed.detail ?? "");
    /* Period detail lives either inside the OCR description or in dedicated
       date fields on the line item. Both participate in duplicate detection. */
    const periodKey = extractPeriodSignature(description, PERIOD_FIELD_KEYS.map((k) => {
      const v = item[k];
      return typeof v === "string" ? v : null;
    }));
    const isStorageFamily = /storage/i.test(parsed.familyName);
    const groupKey = `${normalized}|${rate ?? ""}|${amount ?? ""}|${quantity ?? ""}`;
    const group = groups.get(groupKey) ?? [];
    const provenance = readProvenance(item);

    let evidence: InventoryCharge["duplicateEvidence"] = null;
    const compatible = group.find((existing) => {
      const k = normalizeChargeKey(existing.detail ?? "");
      const detailCompatible = k === detailKey || k === "" || detailKey === "";
      if (!detailCompatible) return false;
      /* Different billing periods = different legitimate charges. */
      if (existing.periodKey !== periodKey) return false;
      /* A storage charge with no recoverable period is never assumed to be a
         re-captured page: storage is billed per period, so identical
         quantity/rate/amount is expected across legitimate occurrences. */
      if (isStorageFamily && periodKey === null) return false;
      /* Extraction provenance decides the ambiguous case. Two identical lines
         printed at DIFFERENT places on the same page are two real charges;
         the same place read twice, or the same page supplied twice, is one
         charge extracted twice. */
      const verdict = provenanceVerdict(existing, provenance);
      if (verdict === "distinct_printed_lines") return false;
      evidence = verdict;
      return true;
    });
    /* A duplicate printed line is NEVER erased: the printed invoice is
       authoritative for what the vendor billed. It is preserved as its own
       source line, flagged as a duplicate suspect, and held out of the
       benchmarked/recovery scope pending vendor clarification. */
    if (compatible) {
      // Prefer the richer label when the duplicate carried the detail.
      if (!compatible.detail && parsed.detail) {
        compatible.detail = parsed.detail;
        compatible.displayName = parsed.displayName;
      }
    }

    const charge: InventoryCharge = {
      id: `charge-${index}-${normalized.slice(0, 40) || "line"}`,
      description,
      displayName: parsed.displayName,
      familyName: parsed.familyName,
      detail: parsed.detail,
      normalized,
      quantity,
      rate,
      amount,
      sourceIndex: index,
      duplicateCount: 1,
      duplicateSuspect: Boolean(compatible),
      duplicateOfChargeId: compatible?.id ?? null,
      duplicateEvidence: compatible ? evidence : null,
      repeatedExtraction:
        Boolean(compatible) &&
        (evidence === "repeated_capture" || evidence === "same_region_reread"),
      sourceFile: provenance.file,
      sourcePage: provenance.page,
      sourceRegion: provenance.region,
      periodKey,
    };
    if (!compatible) {
      group.push(charge);
      groups.set(groupKey, group);
    }
    charges.push(charge);
  });

  markRepeatedBlocks(charges);

  return {
    charges,
    canonical: charges.filter((c) => !c.repeatedExtraction),
    duplicateExtractions: charges.filter((c) => c.repeatedExtraction),
    excluded,
  };
}

/** Match signature for block-repeat detection. */
function blockSignature(c: InventoryCharge): string {
  return `${c.normalized}|${normalizeChargeKey(c.detail ?? "")}|${c.quantity ?? ""}|${c.rate ?? ""}|${c.amount ?? ""}`;
}

/**
 * PROVEN repeated capture without provenance metadata.
 *
 * One printed line repeating is ordinary billing (two helpers, two storage
 * periods). An entire CONSECUTIVE BLOCK of printed lines reappearing verbatim,
 * in the same order, is the same sheet supplied twice — no invoice prints an
 * identical run of three or more lines by coincidence. Only those blocks are
 * held out of the certified scope; single repeats stay in the inventory and
 * are flagged for vendor clarification instead.
 */
const MIN_REPEATED_BLOCK = 3;

function markRepeatedBlocks(charges: InventoryCharge[]): void {
  const sig = charges.map(blockSignature);
  let i = 0;
  while (i < charges.length) {
    let bestStart = -1;
    let bestLen = 0;
    for (let j = 0; j < i; j++) {
      let len = 0;
      while (
        j + len < i &&
        i + len < charges.length &&
        sig[j + len] === sig[i + len]
      ) {
        len++;
      }
      if (len > bestLen) {
        bestLen = len;
        bestStart = j;
      }
    }
    if (bestLen >= MIN_REPEATED_BLOCK) {
      for (let k = 0; k < bestLen; k++) {
        const dup = charges[i + k];
        dup.duplicateSuspect = true;
        dup.repeatedExtraction = true;
        dup.duplicateEvidence = "repeated_capture";
        dup.duplicateOfChargeId = charges[bestStart + k].id;
      }
      i += bestLen;
      continue;
    }
    i++;
  }
}


/**
 * THE canonical scope helper. Any surface holding an already-built charge list
 * uses this instead of re-deriving the rule, so "which charges count" has
 * exactly one definition project-wide.
 */
export function canonicalCharges(charges: InventoryCharge[]): InventoryCharge[] {
  return charges.filter((c) => !c.repeatedExtraction);
}


/**
 * Collapses the inventory into the distinct SERVICE FAMILIES that need market
 * research. Separate billings of the same family share one research result;
 * each charge's expected amount is then computed from its own quantity.
 */
export function chargeFamilies(charges: InventoryCharge[]): Array<{
  key: string;
  familyName: string;
  /** Representative charge used for unit inference. */
  sample: InventoryCharge;
  chargeCount: number;
  totalAmount: number;
}> {
  const map = new Map<string, { key: string; familyName: string; sample: InventoryCharge; chargeCount: number; totalAmount: number }>();
  for (const c of charges) {
    if (c.duplicateSuspect) continue;
    const existing = map.get(c.normalized);
    if (existing) {
      existing.chargeCount += 1;
      existing.totalAmount += c.amount ?? 0;
      continue;
    }
    map.set(c.normalized, {
      key: c.normalized,
      familyName: c.familyName,
      sample: c,
      chargeCount: 1,
      totalAmount: c.amount ?? 0,
    });
  }
  return [...map.values()];
}
